import { useState } from "react";
import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { catalogue } from "./api";

/**
 * Syncing one product by hand (M1.13): paste its ID or its link from the Shopify admin, and it is
 * read from Shopify now rather than when its webhook arrives.
 *
 * For the product somebody has just added or edited and wants to feature in the next minute. The
 * answer says what the copy now holds — in it, taken out of it because it is not active, or not in
 * Shopify at all — because *synced* on its own would not tell an operator whether to go and look.
 */
export function SyncProduct({ onSynced }: { onSynced: () => Promise<void> }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; failed: boolean } | undefined>(
    undefined,
  );

  async function sync() {
    setBusy(true);
    setMessage(undefined);
    try {
      const answer = await catalogue.syncProduct(value);
      const first = answer.entries.find((entry) => !entry.removed);
      setMessage({
        text: t(`catalogue.sync.${answer.outcome}`, {
          title: first?.productTitle ?? "",
          count: answer.entries.filter((entry) => !entry.removed).length,
        }),
        failed: answer.outcome === "absent" || answer.outcome === "removed",
      });
      if (answer.outcome !== "absent") setValue("");
      await onSynced();
    } catch (failure) {
      setMessage({
        text: failure instanceof ApiError ? failure.message : t("shows.unreachable"),
        failed: true,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={4}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && value.trim()) void sync();
        }}
      >
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            size="xs"
            style={{ flex: 1 }}
            label={t("catalogue.sync.label")}
            placeholder={t("catalogue.sync.placeholder")}
            value={value}
            onChange={(event) => setValue(event.currentTarget.value)}
            disabled={busy}
          />
          <Button
            type="submit"
            size="xs"
            variant="default"
            loading={busy}
            disabled={!value.trim()}
          >
            {t("catalogue.sync.button")}
          </Button>
        </Group>
      </form>
      {message ? (
        <Text size="xs" c={message.failed ? "orange" : "green"}>
          {message.text}
        </Text>
      ) : null}
    </Stack>
  );
}
